import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { VerticalScrollableContentWithArrows } from "@/components/ui/vertical-scrollable-content-with-arrows";
import { X, User, Users } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { DashboardItinerary } from "../models";
import { RichTextContent } from "@/components/ui/rich-text-content";

interface ItineraryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  itinerary: DashboardItinerary | null;
  onViewItinerary: (itineraryId: string) => void;
  showButton?: boolean;
}

export const ItineraryDetailsModal = ({
  isOpen,
  onClose,
  itinerary,
  onViewItinerary,
  showButton = true,
}: ItineraryDetailsModalProps) => {
  const { t } = useI18n();

  if (!itinerary) return null;

  // Calcola il numero di persone: priorità ai guests dell'itinerario, poi totalGuests della travel request
  const guestCount = (Array.isArray(itinerary.guestsData) && itinerary.guestsData.length > 0)
    ? itinerary.guestsData.length
    : (itinerary.travelRequest.totalGuests || 1);

  // Calcola il prezzo a persona
  const pricePerPerson = itinerary.total && guestCount > 0
    ? (
      typeof itinerary.total === 'string'
        ? parseFloat((itinerary.total ?? "0").replace(/[^0-9.-]+/g, ""))
        : itinerary.total
    ) / guestCount
    : itinerary.total;

  const formattedPricePerPerson = typeof pricePerPerson === 'number'
    ? pricePerPerson.toFixed(0)
    : pricePerPerson;

  const handleViewItinerary = () => {
    onViewItinerary(itinerary.id);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="xl"
      showHeader={false}
    >
      {/* Pulsante X per chiudere */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white flex items-center justify-center transition-colors shadow-md"
      >
        <X className="h-5 w-5 text-gray-800" />
      </button>

      <div className="flex flex-col w-full h-full lg:flex-row gap-0">
        {/* Immagine a sinistra */}
        <div className="h-[200px] lg:w-[540px] lg:h-[540px] flex-shrink-0 relative">
          <div
            className="absolute inset-0 bg-gray-200 bg-cover bg-center"
            style={{
              backgroundImage: itinerary.image
                ? `url(${itinerary.image})`
                : undefined
            }}
          />
          {!itinerary.image && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <span className="">{t('itinerary.noImage')}</span>
            </div>
          )}
        </div>

        {/* Dettagli a destra */}
        <div className="w-full h-full max-h-full lg:h-[540px] lg:min-w-[400px] px-6 pt-6 pb-3 flex flex-col">

          <div>
            {/* Titolo */}
            <h2 className="text-gray-900 mb-4">
              {itinerary.title || t('itinerary.untitledItinerary')}
            </h2>

            {/* Date, guests, prezzo - 4 colonne */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('common.from')}</p>
                <p className="font-medium text-gray-900">
                  {itinerary.travelRequest.travelStartDate
                    ? new Date(itinerary.travelRequest.travelStartDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('common.to')}</p>
                <p className="font-medium text-gray-900">
                  {itinerary.travelRequest.travelEndDate
                    ? new Date(itinerary.travelRequest.travelEndDate).toLocaleDateString()
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{guestCount === 1 ? t('common.guest') : t('common.guests')}</p>
                <p className="font-medium text-gray-900 flex items-center gap-1">
                  {guestCount === 1 ? <User className="h-4 w-4 text-gray-500" /> : <Users className="h-4 w-4 text-gray-500" />}
                  {guestCount}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('itinerary.pricePerPerson')}</p>
                <p className="font-medium text-gray-900">€ {formattedPricePerPerson}</p>
              </div>
            </div>
          </div>

          {/* Trip Description */}
          {itinerary.description && (
            <>
              <h4 className="text-gray-700 mb-2">
                {t('itinerary.tripDescription')}
              </h4>
              <VerticalScrollableContentWithArrows className="flex-grow flex-shrink mb-4 flex-1" viewportClassName="rounded-lg bg-gray-50 p-3">
                <RichTextContent html={itinerary.description} />
              </VerticalScrollableContentWithArrows>
            </>
          )}

          {/* Bottone View Itinerary */}
          {showButton && (
          <div className="mt-auto">
            <Button
              onClick={handleViewItinerary}
              className="w-full"
              size="lg"
            >
              {t('itinerary.viewItineraryProposal')}
            </Button>
          </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
